import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ITemplateThumbnail } from '@app/features/create-cv/data-structures/create-cv.interface';
import { AppLanguage } from '@app/shared/data-structure/language.interface';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  API_BASE_URL,
  IResumeDuplicateRequest,
  ResumePostRequest,
} from '../data-access/api';

@Injectable({
  providedIn: 'root',
})
export class ResumeService {
  constructor(
    private translateService: TranslateService,
    @Inject(HttpClient) private http: HttpClient,
    @Optional() @Inject(API_BASE_URL) private baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = `${baseUrl}/resume`;
  }

  getResumeTemplateThumbnails(): Observable<ITemplateThumbnail[]> {
    return this.http.get(`${this.baseUrl}/template`).pipe(
      map(
        (response: { templates: ITemplateThumbnail[] }) =>
          response.templates as ITemplateThumbnail[]
      ),
      map((t: ITemplateThumbnail[]) =>
        t.sort(
          (a: ITemplateThumbnail, b: ITemplateThumbnail) =>
            +a.hasColorSchemes - +b.hasColorSchemes
        )
      )
    );
  }

  uploadUserPicture(file: File, endpoint = '/user/me/picture') {
    const formData = new FormData();
    formData.append('uploadedFile', file);
    return this.http.post(`${this.baseUrl}${endpoint}`, formData);
  }

  updateUserPicture(
    pictureName: string,
    file: File,
    endpoint = '/user/me/picture'
  ) {
    if (file instanceof Blob) {
      file = new File([file], pictureName);
    }

    const formData = new FormData();
    formData.append('uploadedFile', file);
    return this.http.put(`${this.baseUrl}${endpoint}/${pictureName}`, formData);
  }

  getUserPicture(
    pictureName: string,
    endpoint = '/user/me/picture'
  ): Observable<Blob | null> {
    return this.http.get(`${this.baseUrl}${endpoint}/${pictureName}`, {
      responseType: 'blob',
    });
  }

  deleteUserPicture(pictureName: string, endpoint = '/user/me/picture') {
    return this.http.delete(`${this.baseUrl}${endpoint}/${pictureName}`);
  }

  getResumes(endpoint = '/user/me') {
    return this.http.get(`${this.baseUrl}${endpoint}`);
  }

  postResume(request: ResumePostRequest, endpoint = '/user/me') {
    return this.http.post(`${this.baseUrl}${endpoint}`, request);
  }

  updateResume(id: string, request: ResumePostRequest, endpoint = '/user/me') {
    return this.http.put(`${this.baseUrl}${endpoint}/${id}`, request);
  }

  deleteResume(id: string, endpoint = '/user/me') {
    return this.http.delete(`${this.baseUrl}${endpoint}/${id}`);
  }

  getResume(id: string, endpoint = '/user/me') {
    return this.http.get(`${this.baseUrl}${endpoint}/${id}`);
  }

  duplicateResume(
    id: string,
    request: IResumeDuplicateRequest,
    endpoint = '/user/me'
  ) {
    return this.http.post(
      `${this.baseUrl}${endpoint}/${id}/duplicate`,
      request
    );
  }

  uploadResume(file: File, endpoint = '/user/me/upload') {
    const formData = new FormData();
    formData.append('uploadedFile', file);
    return this.http.post(`${this.baseUrl}${endpoint}`, formData);
  }

  downloadResume(id: string, endpoint = '/user/me') {
    return this.http.get(`${this.baseUrl}${endpoint}/${id}/download`, {
      responseType: 'blob',
    });
  }

  shareResume(id: string, endpoint = '/user/me') {
    return this.http.post(`${this.baseUrl}${endpoint}/${id}/share`, null);
  }

  unshareResume(id: string, endpoint = '/user/me') {
    return this.http.post(`${this.baseUrl}${endpoint}/${id}/unshare`, null);
  }

  downloadResumeHTML(secretKey: string, endpoint = '/shared') {
    return this.http.get(`${this.baseUrl}${endpoint}/${secretKey}/html`);
  }

  startPolling(id: string, endpoint = '/user/me') {
    return this.http
      .get(`${this.baseUrl}${endpoint}/${id}/polling`)
      .pipe(map((response: any) => response));
  }

  prepareResume(endpoint = '/user/me/prepare') {
    return this.http.get(`${this.baseUrl}${endpoint}`);
  }

  saveResumeFile(resume: Blob, resumeTitle): void {
    // IE/EDGE
    const nav = window.navigator as any;
    if (nav.msSaveOrOpenBlob) {
      nav.msSaveOrOpenBlob(resume, resumeTitle);
      return;
    }

    // IOS
    const isIos = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform);
    if (isIos) {
      // Chrome For iOS
      if (navigator.userAgent.match('CriOS')) {
        const fn = () => {};
        // eslint-disable-next-line  @typescript-eslint/dot-notation
        const saveAs = window['saveAs'] || fn;

        if (
          confirm(
            this.translateService.instant('my_cvs.download_chrome_for_ios')
          )
        ) {
          saveAs(resume, resumeTitle);
        }
        return;
      }

      FileSaver.saveAs(new File([resume], resumeTitle));
    } else {
      const localFileUrl = URL.createObjectURL(resume);
      const a = document.createElement('a');
      a.href = localFileUrl;
      a.download = resumeTitle;
      a.click();
      URL.revokeObjectURL(localFileUrl);
    }
  }

  getResumeLanguages(): Observable<AppLanguage[]> {
    return this.http.get(`${this.baseUrl}/languages`).pipe(
      map(
        (response: { data: { languages: AppLanguage[] } }) =>
          response?.data?.languages
      ),
      catchError(() => of([]))
    );
  }
}
